import React from 'react';
import './OrderPage.sass';

import {Helmet} from 'react-helmet';
import {browserHistory} from 'react-router';

import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {getOrder, getOrderCheckoutFields, getOrderPaymentData, submitOrderCheckoutFields} from '../../ducks/order';
import asSubmissionError from '../../utils/asSubmissionError';

import ErrorPage from '../errorPage/ErrorPage';
import Loader from '../../components/loader/Loader';
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import Order from '../../components/order/Order';

import CheckoutSteps from '../../components/checkout/CheckoutSteps';
import Button from '../../components/button/Button';
import PrimaryButton from '../../components/button/PrimaryButton';
import ConstructedForm from '../../components/constructedForm/ConstructedForm';
import Hexagon from '../../components/hexagon/Hexagon';
import greenTick from './assets/tick-green-big.svg';
import withChannelFormatters from '../channelUtils/withChannelFormatters';
import {getGaClientId, getGaSessionId} from '../../utils/extractGaClientId';
import {analyticsClearEcommerce, analyticsViewOrderDetails} from '../../middlewares/analytics/actions';


const ORDER_POLLING_DELAY_MS = 3000;
const ORDER_POLLING_MAX_AGE_MS = 10 * 60 * 1000;


@injectIntl
@withChannelFormatters
@connect()
export default class OrderPage extends React.PureComponent {
  static getOrderReturnPath = ({id, tokenValue}) =>
    `/order/${id}/${tokenValue}?fromPaymentGateway=yes`;

  constructor(props) {
    super(props);

    this.state = {
      order: null,
      checkoutFields: null,
      checkoutFieldsSubmitted: false
    };

    this.pollTimeout = null;
    this.openedAt = Date.now();
  }

  retryPayment = (order) => this.props.dispatch(getOrderPaymentData(
    order.id,
    order.tokenValue,
    process.env.PUBLIC_URL + this.props.channelFormatters.link(OrderPage.getOrderReturnPath(order)),
    getGaClientId(),
    getGaSessionId()
  )).then(paymentDataResponse => window.location = paymentDataResponse.content.url);

  submitFields = values => this.props.dispatch(submitOrderCheckoutFields(
    this.state.order.id,
    this.state.order.tokenValue,
    values
  ))
    .catch(asSubmissionError)
    .then(() => this.setState({checkoutFieldsSubmitted: true}));

  pollOrderState = (orderId, orderToken, fromPaymentGateway) => {
    const {dispatch, location} = this.props;

    return dispatch(getOrder(orderToken))
      .then(orderResponse => {
          const order = orderResponse.content;

          const [lastPayment] = order.payments.slice(-1);
          const isLastPaymentProcessing = lastPayment.state === 'processing';

          if (isLastPaymentProcessing && (Date.now() - this.openedAt < ORDER_POLLING_MAX_AGE_MS)) {
            this.pollTimeout = setTimeout(
              () => this.pollOrderState(orderId, orderToken, fromPaymentGateway),
              ORDER_POLLING_DELAY_MS
            );
          } else {
            if (fromPaymentGateway) {
              browserHistory.replace(location.pathname);
              dispatch(analyticsClearEcommerce());
              dispatch(analyticsViewOrderDetails(order));
            }

            this.pollTimeout = null;
          }

          this.setState({order});
        },
        () => this.setState({order: false})
      );
  };

  getCheckoutFields = (orderId, orderToken) => {
    const {dispatch} = this.props;

    dispatch(getOrderCheckoutFields(orderId, orderToken))
      .then(fieldsResponse => {
        this.setState({
          checkoutFields: fieldsResponse.content.items,
          checkoutFieldsSubmitted: fieldsResponse.content.items.some(field => !!field.value)
        });
      });
  };

  openIntercom = (evt, message) => {
    evt.preventDefault();

    if (window.Intercom) {
      window.Intercom('showNewMessage', message);
    }
  }

  componentDidMount() {
    const {params, location} = this.props;

    const {fromPaymentGateway} = location.query;
    const {orderId, orderToken} = params;

    this.pollOrderState(orderId, orderToken, fromPaymentGateway);
    this.getCheckoutFields(orderId, orderToken);
  }

  componentWillUnmount() {
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout);
    }
  }

  render() {
    const {intl} = this.props;
    const {order, checkoutFields, checkoutFieldsSubmitted} = this.state;

    if (order === false) {
      return <ErrorPage/>;
    }

    if (!order || !checkoutFields) {
      return <Loader page/>;
    }

    const [lastPayment] = order.payments.slice(-1);
    const isLastPaymentProcessing = lastPayment.state === 'processing';

    const hasCheckoutFields = checkoutFields && !!checkoutFields.length;

    const failedChatMessage = `Hello, I'm experiencing issues with the payment process for my order, Order Number: ${order.number}. The payment failed and I need assistance to complete the transaction. Can you please help?`;

    return (
      <React.Fragment>
        <Helmet>
          <title>{`${intl.formatMessage({id: 'page.order.number'})}${order.number}`}</title>
        </Helmet>

        <div className="container">
          <div className="row">
            <div className="page-checkout">
              <Breadcrumbs links={[{name: `${intl.formatMessage({id: 'page.order.number'})}${order.number}`}]}/>

              <h1>{intl.formatMessage({id: 'page.order.title'})}</h1>

              {order.state !== 'fulfilled' ? (
                isLastPaymentProcessing ? (
                  <div className="checkout">
                    <CheckoutSteps active={CheckoutSteps.Steps.AWAITING_PAYMENT}/>

                    <div className="aftercheckout-error">
                      <h4 className="aftercheckout-error__header">
                        {intl.formatMessage({id: 'page.order.processed.title'})}
                      </h4>

                      <Loader block/>

                      <div className="aftercheckout-error__order">
                        <Order
                          aftercheckout
                          order={order}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="checkout">
                    <CheckoutSteps active={CheckoutSteps.Steps.AWAITING_PAYMENT}/>

                    <div className="aftercheckout-error aftercheckout-error__full">
                      <h4 className="aftercheckout-error__header">
                        {intl.formatMessage({id: 'page.order.failed.title'})}
                      </h4>

                      <div className='aftercheckout-error__info'>
                        <div className='aftercheckout-error__info-block'>
                          <p className="aftercheckout-error__text  aftercheckout-error__text--highlight">
                            {intl.formatMessage({id: 'page.order.failed.list-title'})}
                          </p>

                          <ul className="aftercheckout-error__text">
                            <li>
                              {intl.formatMessage({id: 'page.order.failed.list.first'})}
                            </li>
                            <li>
                              {intl.formatMessage({id: 'page.order.failed.list.second'})}
                            </li>
                            <li>
                              {intl.formatMessage({id: 'page.order.failed.list.third'})}
                            </li>
                            <li>
                              {intl.formatMessage({id: 'page.order.failed.list.fourth'})}
                            </li>
                          </ul>

                          <p className="aftercheckout-error__text">
                            {intl.formatMessage({id: 'page.order.failed.contact-our'})}
                            {' '}
                            <a onClick={(e) => this.openIntercom(e, failedChatMessage)}>
                              {intl.formatMessage({id: 'page.order.failed.support'})}
                            </a>.
                            {' '}
                            {intl.formatMessage({id: 'page.order.failed.contact-our-end'})}
                          </p>
                        </div>

                        <div className='aftercheckout-error__info-block'>
                          <div className="aftercheckout-error__order">
                            <Order
                              aftercheckout
                              order={order}
                            />
                          </div>

                          <div className="aftercheckout-error__actions">
                            <PrimaryButton onClick={() => this.retryPayment(order)}>
                              {intl.formatMessage({id: 'section.generic.retry'})}
                            </PrimaryButton>
                            <Button onClick={(e) => this.openIntercom(e, failedChatMessage)}>
                              {intl.formatMessage({id: 'section.generic.chat-with-us'})}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className="checkout">
                  <CheckoutSteps active={
                    hasCheckoutFields && !checkoutFieldsSubmitted
                      ? CheckoutSteps.Steps.AWAITING_DELIVERY_DETAILS
                      : CheckoutSteps.Steps.IN_PROGRESS
                  }/>

                  <div className="aftercheckout">
                    <div className="aftercheckout__column aftercheckout__column--details">
                      <h4 className="aftercheckout__header">
                        {intl.formatMessage({id: 'page.order.success.title'})}
                      </h4>

                      <p className="aftercheckout__text">
                        {intl.formatMessage({id: 'page.order.success.description'})}
                      </p>

                      {(checkoutFields && !!checkoutFields.length) ? (
                        <p className="aftercheckout__text">
                          {intl.formatMessage({id: 'page.order.success.fill-in'})}
                          <br/>
                          {intl.formatMessage({id: 'page.order.success.contact-you-shortly'})}
                        </p>
                      ) : (
                        <p className="aftercheckout__text">
                          {intl.formatMessage({id: 'page.order.success.contact-you-shortly'})}
                        </p>
                      )}

                      <Order
                        aftercheckout
                        order={order}
                      />
                    </div>

                    {(checkoutFields && !!checkoutFields.length) ? (
                      !checkoutFieldsSubmitted ? (
                        <div className="aftercheckout__column aftercheckout__column--form">
                          <h4 className="aftercheckout__header">
                            {intl.formatMessage({id: 'page.order.info'})}
                          </h4>

                          <ConstructedForm
                            fields={checkoutFields}
                            onSubmit={this.submitFields}
                          />
                        </div>
                      ) : (
                        <div className="aftercheckout__column aftercheckout__column--form-empty">
                          <Hexagon>
                            <img src={greenTick} alt={intl.formatMessage({id: 'page.order.checkmark'})}/>
                          </Hexagon>

                          <h4>
                            {intl.formatMessage({id: 'page.order.submitted.thanks'})}
                          </h4>
                        </div>
                      )
                    ) : (
                      <div className="aftercheckout__column aftercheckout__column--form-empty">
                        <Hexagon>
                          <img src={greenTick} alt={intl.formatMessage({id: 'page.order.checkmark'})}/>
                        </Hexagon>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./containers/orderPage/OrderPage.js